import {User} from '@hconnect/apiclient'
import {useBreakPoints} from '@hconnect/uikit'
import {Alert, Button, TextRegular} from '@hm/fundamental'
import {mdiChevronDown, mdiChevronUp} from '@mdi/js'
import {QueryObserverResult} from '@tanstack/react-query'
import React, {useEffect, useReducer} from 'react'
import {FormProvider, useForm} from 'react-hook-form'

import {
  RoleConfiguration,
  useRolesConfigurationForUserId
} from '../../common/hooks/useRolesConfiguration'
import {useTranslation} from '../../hooks/useTranslation'
import {FormErrors} from '../../layouts/Roles/FormErrors'
import {DataScopeFields} from '../../layouts/Roles/role-edit/DataScopeFields'
import {DataScope} from '../../layouts/Roles/role-view/DataScope'
import {
  ADD_CUSTOMER,
  groupedRoleAssignmentReducer
} from '../../modules/GroupedRoleAssignmentReducer'
import {Permission} from '../../modules/LoggedInUser'
import {GroupedRoleAssignment, RoleAssignment} from '../../modules/ManageUsers.selectors'

import {CollapsedRoleContent} from './components/CollapsedRoleContent'
import {ExpandedSections} from './components/ExpandedSections'
import {RoleEditor} from './components/RoleEditor'
import {RoleHeader} from './components/RoleHeader'
import {useRoleConfiguration} from './hooks/useRoleConfiguration'
import {useRoleFormSubmission} from './hooks/useRoleFormSubmission'
import {useRolesLogic} from './hooks/useRolesLogic'

type RolesViewProps = {
  commonDataScopes: string[]
  groupedRoleAssignment: GroupedRoleAssignment
  isInternal: boolean
  refetchRoles: () => Promise<QueryObserverResult<RoleAssignment[] | null>>
  setShowNewRoleAssignmentForm: (show: boolean) => void
  isAllCollapsed: boolean
  loggedInUserPermissions?: Permission[] | null
  loggedInUserProfile?: User | null
  roleAssignments?: RoleAssignment[] | null
  isNewRole?: boolean
  isUserCreation?: boolean
  unsupportedDataScopes?: string[]
  dataScopeAccessError: string | null
}

export const RolesView = ({
  commonDataScopes,
  groupedRoleAssignment: originalGroupedRoleAssignment,
  loggedInUserPermissions,
  loggedInUserProfile,
  isInternal,
  roleAssignments,
  refetchRoles,
  isNewRole,
  setShowNewRoleAssignmentForm,
  isAllCollapsed,
  isUserCreation = false,
  unsupportedDataScopes,
  dataScopeAccessError
}: RolesViewProps) => {
  const {t} = useTranslation()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)

  const {state, data, utils} = useRolesLogic({
    groupedRoleAssignment: originalGroupedRoleAssignment,
    commonDataScopes,
    loggedInUserPermissions,
    roleAssignments,
    isNewRole,
    isAllCollapsed,
    isUserCreation
  })

  const [groupedRoleAssignment, dispatchRoleStateUpdate] = useReducer(
    groupedRoleAssignmentReducer,
    originalGroupedRoleAssignment
  )

  const formMethods = useForm<GroupedRoleAssignment>({
    defaultValues: groupedRoleAssignment,
    mode: 'onChange'
  })

  const {rolesByType, roleOptions} = useRolesConfigurationForUserId(loggedInUserProfile)
  const roleConfiguration: RoleConfiguration | undefined =
    rolesByType?.[groupedRoleAssignment.roleType]

  const canEdit =
    !!roleConfiguration &&
    Object.keys(groupedRoleAssignment.unsupportedDataScopes).length === 0 &&
    !dataScopeAccessError
  const hasRoleOrders = 'orderIds' in groupedRoleAssignment.unsupportedDataScopes

  const {hasProjectSiteIdScopeInConfiguration, hasContractIdScopeInConfiguration} =
    useRoleConfiguration(roleConfiguration)

  const {handleSubmit} = useRoleFormSubmission({
    customerAdminQueryInfo: data.customerAdminQueryInfo,
    selectedOptions: state.selectedOptions,
    setSelectedOptions: state.setSelectedOptions,
    loggedInUserPermissions: loggedInUserPermissions,
    handleRoleAdded: utils.handleRoleAdded,
    handleRoleUpdated: utils.handleRoleUpdated,
    refetchRoles,
    setIsEditing: state.setIsEditing,
    form: formMethods,
    isUserCreation,
    dataScopeAccessError
  })

  const onSubmit = formMethods.handleSubmit(handleSubmit)

  const {headerItems, titleContent} = CollapsedRoleContent({
    groupedRoleAssignment: originalGroupedRoleAssignment,
    customers: data.customers,
    countryId: data.countryId,
    businessLine: data.businessLine,
    getProjectsSitesLabel: utils.getProjectsSitesLabel,
    hasProjectSiteIdScopeInConfiguration,
    hasContractIdScopeInConfiguration
  })

  useEffect(() => {
    formMethods.reset(groupedRoleAssignment)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedRoleAssignment])

  const renderDataScopes = () => (
    <div
      className={`flex flex-${
        state.isEditing || isMobile ? 'col' : 'row'
      } items-start min-w-0 -ml-${state.isEditing ? 0 : 3} w-full mt-4`}
    >
      {state.isEditing ? (
        <DataScopeFields
          groupedRoleAssignment={groupedRoleAssignment}
          dispatchRoleStateUpdate={dispatchRoleStateUpdate}
          customerAdminData={data.customerAdminData}
          setShowCustomerDropdown={state.setShowCustomerDropdown}
          showCustomerDropdown={state.showCustomerDropdown}
          selectedOptions={state.selectedOptions}
          setSelectedOptions={state.setSelectedOptions}
          isLoadingCustomerAdminOptions={data.isLoadingCustomerAdminOptions}
          loggedInUserPermissions={loggedInUserPermissions}
          loggedInUserProfile={loggedInUserProfile}
        />
      ) : (
        commonDataScopes
          .filter((dataScopeName) => utils.isDatascopeVisible(dataScopeName))
          .map((dataScopeName, index, filteredScopes) => (
            <React.Fragment key={dataScopeName}>
              <DataScope
                dataScopeName={dataScopeName}
                groupedRoleAssignment={originalGroupedRoleAssignment}
              />
              {index < filteredScopes.length - 1 && !isMobile && (
                <div className="self-stretch w-1px border-1px my-5" />
              )}
            </React.Fragment>
          ))
      )}
    </div>
  )

  const newCustomers = data.customerQueryInfo.data

  useEffect(() => {
    if (!data.isUserAdminPilot && newCustomers && newCustomers.length > 0) {
      dispatchRoleStateUpdate({
        type: ADD_CUSTOMER,
        payload: newCustomers.map((customer) => customer.customerId)[0]
      })
      data.customerQueryInfo.remove()
    }
  }, [newCustomers, data.isUserAdminPilot, data.customerQueryInfo])

  useEffect(() => {
    if (!state.isEditing) {
      setShowNewRoleAssignmentForm(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isEditing])

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={onSubmit} data-test-id="edit-role-form">
        <div
          data-test-id={`roleCard-${groupedRoleAssignment.groupId}`}
          className="border-1px border-border_highlight_10 rounded-lg"
        >
          <div className="flex flex-col items-start p-6">
            {!state.isEditing && (
              <RoleHeader
                groupedRoleAssignment={originalGroupedRoleAssignment}
                isInternal={isInternal}
                isCollapsed={state.isCollapsed}
                canEdit={canEdit}
                canDeleteRole={!dataScopeAccessError}
                hasRoleOrders={hasRoleOrders}
                onEditClick={() => {
                  if (dataScopeAccessError) {
                    return
                  }

                  dispatchRoleStateUpdate({
                    type: 'RESET_TO_ORIGINAL',
                    payload: originalGroupedRoleAssignment
                  })
                  state.setIsEditing(!state.isEditing)
                }}
                renderCollapsedHeaderItems={() => headerItems}
                renderCollapsedTitle={() => titleContent()}
                loggedInUserPermissions={loggedInUserPermissions}
                refetchRoles={refetchRoles}
                isUserCreation={isUserCreation}
              />
            )}

            {dataScopeAccessError && (
              <Alert variant="warning" data-cy="role-data-scope-access-warning">
                <TextRegular>{t(dataScopeAccessError)}</TextRegular>
              </Alert>
            )}

            {state.isEditing && (
              <RoleEditor
                groupedRoleAssignment={groupedRoleAssignment}
                dispatchRoleStateUpdate={dispatchRoleStateUpdate}
                roleOptions={roleOptions}
                onCancel={() => {
                  dispatchRoleStateUpdate({
                    type: 'RESET_TO_ORIGINAL',
                    payload: originalGroupedRoleAssignment
                  })
                  state.setIsEditing(false)
                }}
                onSave={onSubmit}
              />
            )}

            {!state.isCollapsed && (
              <>
                {renderDataScopes()}
                <ExpandedSections
                  groupedRoleAssignment={groupedRoleAssignment}
                  dispatchRoleStateUpdate={dispatchRoleStateUpdate}
                  isEditing={state.isEditing}
                  showCustomerInput={state.showCustomerInput}
                  setShowCustomerInput={state.setShowCustomerInput}
                  showCustomerDropdown={state.showCustomerDropdown}
                  setShowCustomerDropdown={state.setShowCustomerDropdown}
                  customerAdminData={data.customerAdminData}
                  loggedInUserPermissions={loggedInUserPermissions}
                  loggedInUserProfile={loggedInUserProfile}
                  isUserAdminPilot={data.isUserAdminPilot}
                  unsupportedDataScopes={unsupportedDataScopes}
                  hasRoleOrders={hasRoleOrders}
                  roleAssignments={roleAssignments}
                />
                <FormErrors />
              </>
            )}
          </div>

          {!state.isEditing && (
            <div className="flex items-center justify-center">
              <Button
                variant="tertiary"
                size="extraSmall"
                type="button"
                onClick={() => state.setIsCollapsed(!state.isCollapsed)}
                icon={state.isCollapsed ? mdiChevronDown : mdiChevronUp}
                iconPosition="left"
                data-cy={`toggle-${state.isCollapsed ? 'expand' : 'collapse'}-button-${
                  groupedRoleAssignment.groupId
                }`}
              >
                {state.isCollapsed ? t('roleAssignment.expand') : t('roleAssignment.collapse')}
              </Button>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
